import { Virtualizer, useVirtualizer } from "@tanstack/react-virtual";

import React from "react";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

const estimateSizeFn = () => 56;

interface Identifiable {
  id: string | number;
}

export interface VirtualizedContentProps<TData> {
  data: TData[];
  hasPages?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  reverse?: boolean;
  estimateSize?: (index: number) => number;
  overscan?: number;
}

export function useVirtualizedContent<TData extends Identifiable>({
  data,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  hasPages,
  reverse,
  estimateSize = estimateSizeFn,
  overscan = 5,
}: VirtualizedContentProps<TData>) {
  const parentRef = React.useRef<HTMLDivElement | null>(null);
  const virtualizerRef =
    React.useRef<Virtualizer<HTMLDivElement, Element>>(null);

  const getItemKey = React.useCallback(
    (index: number) => data[index]?.id,
    [data],
  );

  const count = React.useMemo(
    () => (hasPages && hasNextPage ? data.length + 1 : data.length),
    [hasPages, hasNextPage, data.length],
  );

  const virtualizer = useVirtualizer({
    count,
    overscan,
    getScrollElement: () => parentRef.current,
    estimateSize,
    getItemKey,
  });
  const virtualItems = virtualizer.getVirtualItems();

  useIsomorphicLayoutEffect(() => {
    virtualizerRef.current = virtualizer;
  });

  // Fetch next page when scrolled to top
  React.useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];

    if (!lastItem || !hasPages) {
      return;
    }

    if (
      lastItem.index >= data.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage?.();
    }
  }, [
    hasPages,
    hasNextPage,
    fetchNextPage,
    data.length,
    isFetchingNextPage,
    virtualItems,
  ]);

  // Invert scroll direction because of the inverted virtualizer
  React.useEffect(() => {
    if (!reverse) {
      return;
    }

    const handleScroll = (e: WheelEvent) => {
      e.preventDefault();
      const currentTarget = e.currentTarget as HTMLElement;

      if (currentTarget) {
        currentTarget.scrollTop -= e.deltaY;
      }
    };
    parentRef.current?.addEventListener("wheel", handleScroll, {
      passive: false,
    });
    const ref = parentRef.current;
    return () => {
      ref?.removeEventListener("wheel", handleScroll);
    };
  }, [reverse]);

  return {
    parentRef,
    virtualizer,
  };
}
